import { isStepActionable } from "@/react/components/Paywalls/Checkout/helpers/sessions/StepStatusHelper";
import { getCheckoutRedirectUrl } from "@/react/components/Paywalls/CheckoutWithSession/helpers/ReturnToUrlHelper";
import type {
  CheckoutSessionStep,
  CheckoutSessionStepOffer,
  CheckoutSession,
  CheckoutSessionStepStatus,
  CheckoutSessionPaywall,
  CheckoutSessionStepPaywallPrice,
} from "@/react/types/Paywalls/Checkout/Session";

export const getStepById = (
  steps: CheckoutSessionStep[],
  id: number,
): CheckoutSessionStep | undefined => steps.find(step => step.id === id);

const getStepBySequence = (
  steps: CheckoutSessionStep[],
  sequence: number,
): CheckoutSessionStep | undefined =>
  steps.find(step => step.sequence === sequence);

export const getMainStep = (
  steps: CheckoutSessionStep[],
): CheckoutSessionStep | undefined => getStepBySequence(steps, 0);

export const getStepsByStatus = (
  steps: CheckoutSessionStep[],
  statuses: Array<CheckoutSessionStepStatus>,
): CheckoutSessionStep[] =>
  steps.filter(step => statuses.includes(step.status));

export const getNextAvailableOfferAfterMain = (
  steps: CheckoutSessionStep[] | undefined,
  currentStepId?: number,
): CheckoutSessionStep | undefined => {
  if (!steps) return undefined;

  return steps
    .filter(
      step =>
        step.sequence > 0 &&
        isStepActionable(step) &&
        (!currentStepId || currentStepId !== step.id),
    )
    .sort((a, b) => a.sequence - b.sequence)[0];
};

export const getStepByPaywallId = (
  paywallId: number,
  session: CheckoutSession | null | undefined,
): CheckoutSessionStep | undefined =>
  session?.steps.find(step =>
    step.offers.some(offer => offer.paywall.id === paywallId),
  );

export const getMainOffer = (
  step: CheckoutSessionStep | undefined,
): CheckoutSessionStepOffer | undefined =>
  step?.offers?.find(offer => offer.main);

export const getMainPaywallFromStep = (step?: CheckoutSessionStep) =>
  step?.offers[0]?.paywall;

const getMainPaywallPrice = (
  session: CheckoutSession | null | undefined,
): CheckoutSessionStepPaywallPrice | undefined =>
  session?.steps[0]?.community_member_subscription?.paywall_price ||
  session?.steps[0]?.community_member_charge?.paywall_price;

export const getMainPaywall = (
  session: CheckoutSession | null | undefined,
): CheckoutSessionPaywall | undefined =>
  getMainOffer(session?.steps[0])?.paywall;

export const getMainPaywallId = (
  session: CheckoutSession | null | undefined,
): number | undefined => getMainOffer(session?.steps[0])?.paywall.id;

export const getCoupon = (
  stepSession: CheckoutSessionStep | undefined,
  offerIndex: number = 0,
) => {
  const offer = stepSession?.offers[offerIndex];
  return offer?.paywall_offer?.paywall_coupon?.code || "";
};

export const getPaywallOffer = (
  stepSession: CheckoutSessionStep | undefined,
  offerIndex: number = 0,
) => {
  const offer = stepSession?.offers[offerIndex];
  return offer?.paywall_offer;
};

export const getOfferedPaywall = (
  stepSession: CheckoutSessionStep | undefined,
  offerIndex: number = 0,
) => {
  const offer = stepSession?.offers[offerIndex];
  return offer?.paywall;
};

export const getResultingPurchaseFromStepSession = (
  stepSession?: CheckoutSessionStep | null,
) => {
  if (!stepSession) {
    return null;
  }

  return (
    stepSession.community_member_subscription ||
    stepSession.community_member_charge
  );
};

export const getPaywallPriceFromRecord = (
  record: ReturnType<typeof getResultingPurchaseFromStepSession>,
) => {
  if (!record) {
    return null;
  }

  return record.paywall_price;
};

export const returnCreatedAtDateFromRecord = (
  record?: ReturnType<typeof getResultingPurchaseFromStepSession>,
): Date => (record?.created_at ? new Date(record.created_at) : new Date());

export const getRedirectUrlFromMainPaywallAndPaywallPrice = (
  session: CheckoutSession | null | undefined,
) => {
  const paywall = getMainPaywall(session);
  const paywallPrice = getMainPaywallPrice(session);

  return getCheckoutRedirectUrl({
    returnTo: session?.settings?.return_to,
    eventRedirectUrl: paywallPrice?.redirect_event_url,
    paywallRedirectUrl: paywall?.redirect_url,
    communityRootUrl: session?.settings?.community_root_url,
    currentOrigin: window.location.origin,
  });
};
